import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/config';
import '../styles/AdminDashboard.css';
import Users from './Users';
import Questions from './Questions';
import Upload from './Upload';
import MathRenderer, { hasMath } from '../components/MathRenderer';

function MathOrText({ text, subjectId, block = false, className = '' }) {
  if (!text) return null;
  if (hasMath(text, subjectId))
    return <MathRenderer text={text} subjectId={subjectId} block={block} className={className} />;
  return <span className={className}>{text}</span>;
}

const JIT_LOGO = '/images/jit-logo.png';

// ── Admin Edit Modal ──────────────────────────────────────────────────────────
function AdminEditModal({ question, modules, onClose, onSaved }) {
  const q = question;
  const BLOOMS_OPTS = [
    { value:'remember',   label:'1 - Remember'  },
    { value:'understand', label:'2 - Understand' },
    { value:'apply',      label:'3 - Apply'      },
    { value:'analyse',    label:'4 - Analyse'    },
    { value:'evaluate',   label:'5 - Evaluate'   },
    { value:'create',     label:'6 - Create'     },
  ];
  const OPT_KEYS   = ['a','b','c','d'];
  const OPT_FIELDS = ['option_a','option_b','option_c','option_d'];

  const [form, setForm] = useState({
    question_text      : q.question_text    || '',
    option_a           : q.option_a         || '',
    option_b           : q.option_b         || '',
    option_c           : q.option_c         || '',
    option_d           : q.option_d         || '',
    correct_answer     : (q.correct_answer  || 'A').toUpperCase(),
    explanation        : q.explanation      || '',
    difficulty_level   : q.difficulty_level || '',
    blooms_index       : q.blooms_index      || '',
    module_id          : String(q.module_id || ''),
    reverification_note: '',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));
  const curAnswer = form.correct_answer.toLowerCase();
  const curDiff   = form.difficulty_level;

  const handleSave = async () => {
    if (!form.reverification_note.trim()) {
      setErr('Please add a note explaining what you changed.');
      return;
    }
    setSaving(true); setErr('');
    try {
      await API.post('/api/verify/admin/edit-and-reverify', {
        question_id    : q.question_id,
        ...form,
        correct_answer : form.correct_answer.toUpperCase(),
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div
      style={{
        position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
        display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999,
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background:'#fff', borderRadius:14, width:'90%', maxWidth:820,
        maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'18px 24px', borderBottom:'1px solid #e2e8f0',
        }}>
          <span style={{ fontWeight:800, fontSize:16 }}>
            ✏️ Admin Edit — Q{q.question_number ?? q.question_id}
          </span>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#94a3b8' }}>
            ✕
          </button>
        </div>

        {err && (
          <div style={{
            margin:'12px 24px 0', padding:'10px 14px',
            background:'#fef2f2', border:'1px solid #fca5a5',
            borderRadius:8, color:'#b91c1c', fontSize:13,
          }}>{err}</div>
        )}

        <div style={{ padding:'20px 24px', display:'flex', gap:24 }}>
          <div style={{ flex:1.5 }}>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#000', marginBottom:6 }}>Question</div>
              <textarea
                style={{
                  width:'100%', borderRadius:8, border:'1px solid #e2e8f0',
                  padding:'10px 14px', fontSize:14, resize:'none', outline:'none',
                  fontFamily:'monospace', boxSizing:'border-box', lineHeight:1.6, overflow:'hidden',
                }}
                value={form.question_text}
                onChange={e => f('question_text', e.target.value)}
                ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }}}
                onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              />
            </div>

            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#000', marginBottom:6 }}>Options</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {OPT_KEYS.map((opt, i) => {
                  const fieldKey  = OPT_FIELDS[i];
                  const isCorrect = curAnswer === opt;
                  return (
                    <div key={opt} style={{
                      display:'flex', alignItems:'center', gap:8,
                      border: isCorrect ? '1.5px solid #10b981' : '1px solid #e2e8f0',
                      borderRadius:8, padding:'8px 10px',
                      background: isCorrect ? '#f0fdf4' : '#fff',
                      overflow:'hidden', minWidth:0,
                    }}>
                      <span style={{ fontWeight:700, color:'#64748b', fontSize:13, minWidth:18 }}>{opt})</span>
                      <textarea
                        value={form[fieldKey]}
                        onChange={e => f(fieldKey, e.target.value)}
                        ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }}}
                        onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                        style={{
                          flex:1, border:'none', outline:'none', background:'transparent',
                          fontSize:13, fontFamily:'monospace',
                          width:'100%', minWidth:0, resize:'none', lineHeight:1.5, overflow:'hidden',
                        }}
                      />
                      {isCorrect && <span style={{ color:'#10b981' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#000', marginBottom:6 }}>Explanation</div>
              <textarea
                style={{
                  width:'100%', borderRadius:8, border:'1px solid #e2e8f0',
                  padding:'10px 14px', fontSize:14, resize:'none', outline:'none',
                  fontFamily:'monospace', boxSizing:'border-box', lineHeight:1.6, overflow:'hidden',
                }}
                value={form.explanation}
                onChange={e => f('explanation', e.target.value)}
                ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }}}
                onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              />
            </div>

            {q.admin_reverification_note && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#92400e', marginBottom:6 }}>
                  📝 Previous Note Sent To Verifier
                </div>
                <div style={{
                  background:'#fef3c7', border:'1px solid #d97706',
                  borderRadius:8, padding:'10px 14px', fontSize:13, color:'#1e293b', lineHeight:1.6,
                }}>
                  {q.admin_reverification_note}
                </div>
              </div>
            )}

            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'#dc2626', marginBottom:6 }}>
                Note To Verifier (Required) *
              </div>
              <textarea
                style={{
                  width:'100%', borderRadius:8, border:'1.5px solid #fca5a5',
                  padding:'10px 12px', fontSize:14, resize:'vertical', minHeight:80,
                  outline:'none', fontFamily:'inherit', boxSizing:'border-box',
                }}
                placeholder="Explain what you changed and why (verifier will see this)..."
                value={form.reverification_note}
                onChange={e => f('reverification_note', e.target.value)}
              />
            </div>
          </div>

          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'#000', marginBottom:8 }}>Module</div>
              <select
                style={{
                  width:'100%', padding:'8px 12px', borderRadius:8,
                  border:'1px solid #e2e8f0', fontSize:14, background:'#fff',
                }}
                value={form.module_id}
                onChange={e => f('module_id', e.target.value)}
              >
                <option value="">— Select Module —</option>
                {modules
                  .filter(m => !q.subject_id || String(m.subject_id) === String(q.subject_id))
                  .map(m => (
                    <option key={m.module_id} value={String(m.module_id)}>{m.name}</option>
                  ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'#000', marginBottom:8 }}>Correct Answer</div>
              <div style={{ display:'flex', gap:8 }}>
                {OPT_KEYS.map(opt => (
                  <button key={opt} type="button"
                    onClick={() => f('correct_answer', opt.toUpperCase())}
                    style={{
                      width:40, height:40, borderRadius:8, fontWeight:700, fontSize:14,
                      cursor:'pointer', border:'1.5px solid',
                      borderColor: curAnswer === opt ? '#6366f1' : '#e2e8f0',
                      background:  curAnswer === opt ? '#6366f1' : '#f8fafc',
                      color:       curAnswer === opt ? '#fff'    : '#64748b',
                    }}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'#000', marginBottom:8 }}>Difficulty Level</div>
              <div style={{ display:'flex', gap:8 }}>
                {['low','medium','high'].map(d => {
                  const colors = {
                    low   : { bg:'#dcfce7', border:'#16a34a', color:'#15803d' },
                    medium: { bg:'#fef3c7', border:'#d97706', color:'#92400e' },
                    high  : { bg:'#fee2e2', border:'#dc2626', color:'#991b1b' },
                  };
                  const active = curDiff === d;
                  return (
                    <button key={d} type="button"
                      onClick={() => f('difficulty_level', d)}
                      style={{
                        padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer',
                        border: active ? `1px solid ${colors[d].border}` : '1px solid #e2e8f0',
                        background: active ? colors[d].bg : '#f8fafc',
                        color: active ? colors[d].color : '#64748b',
                      }}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'#000', marginBottom:8 }}>Bloom's Index</div>
              <select
                style={{
                  width:'100%', padding:'8px 12px', borderRadius:8,
                  border:'1px solid #e2e8f0', fontSize:14, background:'#fff',
                }}
                value={form.blooms_index}
                onChange={e => f('blooms_index', e.target.value)}
              >
                <option value="">— Select —</option>
                {BLOOMS_OPTS.map(b => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </div>

            <div style={{
              background:'#eff6ff', border:'1px solid #bfdbfe',
              borderRadius:8, padding:'12px 14px', fontSize:12, color:'#1e40af', lineHeight:1.6,
            }}>
              ℹ️ After saving, this question will be sent back to the original verifier for re-verification.
              It will reappear in their queue with your note.
            </div>
          </div>
        </div>

        <div style={{
          display:'flex', justifyContent:'flex-end', gap:10,
          padding:'16px 24px', borderTop:'1px solid #e2e8f0',
        }}>
          <button onClick={onClose} disabled={saving}
            style={{
              padding:'9px 20px', borderRadius:8, border:'1px solid #e2e8f0',
              background:'#f8fafc', color:'#475569', fontSize:14, fontWeight:600, cursor:'pointer',
            }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{
              padding:'9px 20px', borderRadius:8, border:'none',
              background: saving ? '#a5b4fc' : '#6366f1',
              color:'#fff', fontSize:14, fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer',
            }}>
            {saving ? 'Saving…' : '💾 Save & Send for Reverification'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── VerificationsPanel ────────────────────────────────────────────────────────
function VerificationsPanel() {
  const [log,        setLog]        = useState([]);
  const [summary,    setSummary]    = useState([]);
  const [subjects,   setSubjects]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);

  const [notePopup, setNotePopup] = useState(null);
  const [editingQ,  setEditingQ]  = useState(null);
  const [modules,   setModules]   = useState([]);

  const [filterUser, setFilterUser] = useState('');
  const [filterAct,  setFilterAct]  = useState('');
  const [filterSubj, setFilterSubj] = useState('');

  useEffect(() => {
    API.get('/api/subjects').then(r => setSubjects(r.data || [])).catch(() => {});
    API.get('/api/verify/admin/log', { params: { page: 1, limit: 999, summary_only: true } })
      .then(r => setSummary(r.data.summary || []))
      .catch(() => {
        API.get('/api/verify/admin/log', { params: { page: 1, limit: 20 } })
          .then(r => setSummary(r.data.summary || []))
          .catch(() => {});
      });
    load(1, '', '', '');
    API.get('/api/verify/modules').then(r => setModules(r.data.modules || [])).catch(() => {});
  }, []); // eslint-disable-line

  const load = async (pg, uid, act, subj) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (uid)  params.user_id    = uid;
      if (act)  params.action     = act;
      if (subj) params.subject_id = subj;
      const res = await API.get('/api/verify/admin/log', { params });
      setLog(res.data.log      || []);
      setTotal(res.data.total  || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(pg);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilterUser(''); setFilterAct(''); setFilterSubj('');
    load(1, '', '', '');
  };

  const fmtDateOnly = dt => dt
    ? new Date(dt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
    : null;
  const fmtTimeOnly = dt => dt
    ? new Date(dt).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
    : null;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Verification History</h2>
          <p className="page-subtitle">Complete audit trail — who verified, who approved, and when</p>
        </div>
        <span className="vlog-total-badge">{total} Record{total !== 1 ? 's' : ''}</span>
      </div>

      <div className="vlog-filters">
        <select className="vlog-select" value={filterUser || ''}
          onChange={e => { const v = e.target.value; setFilterUser(v); load(1, v, filterAct, filterSubj); }}>
          <option value="">All Verifiers</option>
          {summary.map(u => <option key={u.user_id} value={String(u.user_id)}>{u.name}</option>)}
        </select>
        <select className="vlog-select" value={filterSubj || ''}
          onChange={e => { const v = e.target.value; setFilterSubj(v); load(1, filterUser, filterAct, v); }}>
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s.subject_id} value={String(s.subject_id)}>{s.name}</option>)}
        </select>
        <select className="vlog-select" value={filterAct || ''}
          onChange={e => { const v = e.target.value; setFilterAct(v); load(1, filterUser, v, filterSubj); }}>
          <option value="">All Statuses</option>
          <option value="approved">Verified</option>
          <option value="flagged">Flagged</option>
        </select>
        <button className="btn-reset" onClick={handleReset} disabled={loading}>✕ Reset</button>
      </div>

      <div className="table-container">
        <div className="table-scroll">
          <table>
            <colgroup>
              <col className="col-num" />
              <col className="col-question" />
              <col className="col-subject" />
              <col className="col-verifier" />
              <col className="col-ts" />
              <col className="col-verifier" />
              <col className="col-ts" />
              <col className="col-hod" />  
              <col className="col-ts" />
              <col className="col-feedback" />
              <col className="col-status" />
              <col style={{ width:80 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="col-num">Q No.</th>
                <th className="col-question">Question</th>
                <th className="col-subject">Subject</th>
                <th className="col-verifier">Verified By</th>
                <th className="col-ts">Verified At</th>
                <th className="col-verifier"> Edited By Admin</th>
                <th className="col-ts">Admin Edited At</th>
                <th className="col-hod">HOD Approved By</th>
                <th className="col-ts">HOD Approved At</th>
                <th className="col-feedback">Feedback</th>
                <th className="col-status">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="loading-text">Loading…</td></tr>}
              {!loading && log.length === 0 && <tr><td colSpan={11} className="loading-text">No Records Found.</td></tr>}
              {!loading && log.map((row) => (
                <tr key={row.verification_id}>
                  <td className="col-num">Q{row.question_number ?? row.question_id}</td>

                  <td className="col-question">
                    <span className="vlog-qtext">
                      <MathOrText text={row.question_text} subjectId={row.subject_id} />
                    </span>
                    <div className="vlog-tag-row">
                      {row.exam_year && (
                        <span className="badge badge-role vlog-year-badge">{row.exam_type} {row.exam_year}</span>
                      )}
                      {row.module_name  && <span className="vlog-tag vlog-tag--module">{row.module_name}</span>}
                      {row.correct_answer && <span className="vlog-tag vlog-tag--ans">Ans: {row.correct_answer.toUpperCase()}</span>}
                      {row.blooms_index && (
                        <span className="vlog-tag vlog-tag--bloom">
                          {row.blooms_index.charAt(0).toUpperCase() + row.blooms_index.slice(1)}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="col-subject">
                    <span className="vlog-subj-pill" title={row.subject_name || '—'}>{row.subject_name || '—'}</span>
                  </td>

                <td className="col-verifier">
  {row.verified_by_name
    ? <span className="vlog-name" title={row.verified_by_name}>{row.verified_by_name}</span>
    : <span className="vlog-none">—</span>}
</td>

<td className="col-ts">
  {row.verified_at ? (
    <><span className="vlog-ts-date">{fmtDateOnly(row.verified_at)}</span>
      <span className="vlog-ts-time">{fmtTimeOnly(row.verified_at)}</span></>
  ) : <span className="vlog-none">—</span>}
</td>

<td className="col-verifier">
  {row.admin_reverification_note
    ? <span className="vlog-name">Admin</span>
    : <span className="vlog-none">—</span>}
</td>

<td className="col-ts">
  {row.admin_edited_at ? (
    <><span className="vlog-ts-date">{fmtDateOnly(row.admin_edited_at)}</span>
      <span className="vlog-ts-time">{fmtTimeOnly(row.admin_edited_at)}</span></>
  ) : <span className="vlog-none">—</span>}
</td>

                  <td className="col-hod">
                    {row.hod_approved_by_name
                      ? <span className="vlog-name" title={row.hod_approved_by_name}>{row.hod_approved_by_name}</span>
                      : <span className="vlog-none">—</span>}
                  </td>

                  <td className="col-ts">
                    {row.hod_approved_at ? (
                      <><span className="vlog-ts-date">{fmtDateOnly(row.hod_approved_at)}</span>
                        <span className="vlog-ts-time">{fmtTimeOnly(row.hod_approved_at)}</span></>
                    ) : <span className="vlog-none">—</span>}
                  </td>

                  <td className="col-feedback">
                    {(row.feedback_pdf || (row.feedback_note && row.feedback_note.trim())) ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-start' }}>
                        {row.feedback_pdf && (
                          <a href={`${API.defaults.baseURL}${row.feedback_pdf}`}
                            className="vlog-pdf-link" title="View Feedback PDF"
                            onClick={e => { e.preventDefault(); window.open(`${API.defaults.baseURL}${row.feedback_pdf}`, '_blank', 'noopener,noreferrer'); }}>
                            📄 PDF
                          </a>
                        )}
                        {row.feedback_note && row.feedback_note.trim() && (
                          <span className="vlog-note-only" style={{ cursor:'pointer' }}
                            title="Click to view full note"
                            onClick={() => setNotePopup({ text: row.feedback_note, qNum: row.question_number ?? row.question_id })}>
                            💬 Note
                          </span>
                        )}
                      </div>
                    ) : <span className="vlog-none">—</span>}
                  </td>

                  <td className="col-status">
                    {row.hod_approved
                      ? <span className="vlog-status vlog-status--approved">HOD Approved</span>
                      : row.action === 'flagged'
                        ? <span className="vlog-status vlog-status--flagged">Flagged</span>
                        : <span className="vlog-status vlog-status--verified">Verified</span>}
                  </td>

                  <td>
                    <button onClick={() => setEditingQ(row)}
                      style={{
                        padding:'5px 14px', borderRadius:6, border:'1px solid #e2e8f0',
                        background:'#f8fafc', color:'#475569', fontSize:12,
                        fontWeight:600, cursor:'pointer',
                      }}>
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="vlog-pagination">
          <button className="btn-primary" style={{ padding:'8px 18px' }}
            onClick={() => load(page - 1, filterUser, filterAct, filterSubj)}
            disabled={page === 1 || loading}>← Prev</button>
          <span style={{ fontSize:13, color:'var(--text-muted)' }}>Page {page} Of {totalPages}</span>
          <button className="btn-primary" style={{ padding:'8px 18px' }}
            onClick={() => load(page + 1, filterUser, filterAct, filterSubj)}
            disabled={page === totalPages || loading}>Next →</button>
        </div>
      )}

      {notePopup && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999,
        }} onClick={() => setNotePopup(null)}>
          <div style={{
            background:'#fff', border:'1px solid #e2e8f0', borderRadius:14,
            padding:'24px 28px', maxWidth:500, width:'90%', boxSizing:'border-box',
            boxShadow:'0 20px 60px rgba(0,0,0,0.18)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <span style={{ fontWeight:700, fontSize:15, color:'#0d1f2d' }}>
                💬 Feedback Note — Q{notePopup.qNum}
              </span>
              <button onClick={() => setNotePopup(null)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#94a3b8', lineHeight:1, padding:'0 4px' }}>
                ✕
              </button>
            </div>
            <div style={{ background:'#f8fafc', borderRadius:8, border:'1px solid #e2e8f0', padding:'14px 16px' }}>
              <p style={{ margin:0, fontSize:14, color:'#1e293b', lineHeight:1.75, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
                {notePopup.text}
              </p>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
              <button onClick={() => setNotePopup(null)}
                style={{ padding:'7px 20px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', border:'1px solid #cbd5e1', background:'#f1f5f9', color:'#334155' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {editingQ && (
        <AdminEditModal
          question={editingQ}
          modules={modules}
          onClose={() => setEditingQ(null)}
          onSaved={() => { setEditingQ(null); load(page, filterUser, filterAct, filterSubj); }}
        />
      )}
    </div>
  );
}

// ── FeedbackReportPanel ───────────────────────────────────────────────────────
function FeedbackReportPanel() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    API.get('/api/verify/admin/feedback-report')
      .then(r => setData(r.data.subjects || []))
      .catch(e => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-text" style={{ padding:48 }}>Loading…</div>;
  if (error)   return <div className="error-box">{error}</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">💬 Feedback Report</h2>
          <p className="page-subtitle">Faculty-wise feedback, admin edits, reverifications and HOD approvals</p>
        </div>
      </div>

      {data.map(subject => {
        const totalFb  = subject.faculty.reduce((s, f) => s + f.total_feedback, 0);
const totalAe  = subject.faculty.reduce((s, f) => s + (parseInt(f.total_admin_edits) || 0), 0);
const totalRv  = subject.faculty.reduce((s, f) => s + f.total_reverified, 0);
const totalHod = subject.faculty.reduce((s, f) => s + f.total_hod_approved_after_reverify, 0);
const totalRemaining = totalFb - totalHod;

        return (
          <div key={subject.subject_id} style={{ marginBottom:36 }}>
            <h3 style={{ fontSize:15, fontWeight:800, color:'var(--text)', marginBottom:12 }}>
              {subject.subject_name} Feedback Report
            </h3>

            <div className="vrep-kpi-row">
              <div className="vrep-kpi-card vrep-kpi-teal">
                <div className="vrep-kpi-icon vrep-kpi-icon--teal">💬</div>
                <div className="vrep-kpi-body">
                  <span className="vrep-kpi-label">Total Feedbacks</span>
                  <span className="vrep-kpi-value">{totalFb}</span>
                </div>
              </div>
              <div className="vrep-kpi-card vrep-kpi-blue">
                <div className="vrep-kpi-icon">✏️</div>
                <div className="vrep-kpi-body">
                  <span className="vrep-kpi-label">Admin Edits</span>
                  <span className="vrep-kpi-value">{totalAe}</span>
                </div>
              </div>
              <div className="vrep-kpi-card vrep-kpi-purple">
                <div className="vrep-kpi-icon vrep-kpi-icon--purple">🔄</div>
                <div className="vrep-kpi-body">
                  <span className="vrep-kpi-label">Reverified</span>
                  <span className="vrep-kpi-value">{totalRv}</span>
                </div>
              </div>
              <div className="vrep-kpi-card vrep-kpi-orange">
                <div className="vrep-kpi-icon">🛡️</div>
                <div className="vrep-kpi-body">
                  <span className="vrep-kpi-label">HOD Approved</span>
                  <span className="vrep-kpi-value">{totalHod}</span>
                </div>
              
               </div>

                <div className="vrep-kpi-card vrep-kpi-orange" style={{ flex: '0 0 auto', minWidth: 130 }}>         
                <div className="vrep-kpi-icon">⏳</div>
                <div className="vrep-kpi-body">
                  <span className="vrep-kpi-label">Remaining</span>
                  <span className="vrep-kpi-value" style={{ color: totalRemaining > 0 ? '#dc2626' : '#15803d' }}>
                    {totalRemaining}
                  </span>
                </div>
              </div>
            </div>






            <div className="table-container vrep-table-container" style={{ maxWidth:700 }}>
              <div className="vrep-table-header">
                <span className="vrep-table-header-icon">🗒️</span>
                <span className="vrep-table-header-title">Faculty Feedback Summary</span>
              </div>
              <div className="table-scroll vrep-report-scroll">
                <table>
                  <colgroup>
                    <col style={{ width:50 }} />
                    <col style={{ width:160 }} />
                    <col style={{ width:100 }} />
                    <col style={{ width:100 }} />
                    <col style={{ width:100 }} />
                    <col style={{ width:120 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Sl.</th>
                      <th>Faculty</th>
                      <th style={{ textAlign:'center' }}>Feedbacks</th>
                      <th style={{ textAlign:'center' }}>Admin Edits</th>
                      <th style={{ textAlign:'center' }}>Reverified</th>
                      <th style={{ textAlign:'center' }}>HOD Approved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subject.faculty.length === 0 ? (
                      <tr><td colSpan={6} className="loading-text">No data yet</td></tr>
                    ) : (
                      <>
                        {subject.faculty.map((f, i) => (
                          <tr key={f.user_id}>
                            <td>{i + 1}</td>
                            <td>{f.faculty_name}</td>
                            <td style={{ textAlign:'center' }}>{f.total_feedback}</td>
                            <td style={{ textAlign:'center' }}>{parseInt(f.total_admin_edits) || 0}</td>
                            <td style={{ textAlign:'center' }}>{f.total_reverified}</td>
                            <td style={{ textAlign:'center' }}>{f.total_hod_approved_after_reverify}</td>
                          </tr>
                        ))}
                        <tr className="vrep-total-row">
                          <td></td>
                          <td>Total</td>
                          <td style={{ textAlign:'center' }} className="vrep-total-verified">{totalFb}</td>
                          <td style={{ textAlign:'center' }}>{totalAe}</td>
                          <td style={{ textAlign:'center' }} className="vrep-total-approved">{totalRv}</td>
                          <td style={{ textAlign:'center' }}>{totalHod}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ReportsPanel ──────────────────────────────────────────────────────────────
// Loads ALL subject reports at once (no subject picker needed).
// FIX: removed the undefined `subject` variable reference that was
// crashing the component before it could render any data.
function ReportsPanel() {
  const REPORT_DEFS = [
    { key:'physics',     label:'Physics',     icon:'⚡', match:'physics'   },
    { key:'chemistry',   label:'Chemistry',   icon:'⚗️',  match:'chemistry' },
    { key:'mathematics', label:'Mathematics', icon:'📐', match:'math'      },
  ];

  const [subjects,    setSubjects]    = useState([]);
  const [reportsData, setReportsData] = useState({});
  const [loadingMap,  setLoadingMap]  = useState({});
  const [errorMap,    setErrorMap]    = useState({});

  useEffect(() => {
    API.get('/api/subjects')
      .then(r => setSubjects(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!subjects.length) return;
    REPORT_DEFS.forEach(def => {
      const subject = subjects.find(s => (s.name || '').toLowerCase().includes(def.match));
      if (!subject) {
        setErrorMap(prev => ({ ...prev, [def.key]: `No subject matching "${def.label}" found.` }));
        return;
      }
      setLoadingMap(prev => ({ ...prev, [def.key]: true }));
      API.get('/api/verify/admin/reports', { params: { subject_id: subject.subject_id } })
        .then(res => setReportsData(prev => ({ ...prev, [def.key]: res.data })))
        .catch(err => setErrorMap(prev => ({
          ...prev,
          [def.key]: err.response?.data?.message || err.response?.data?.error || 'Failed to load report.',
        })))
        .finally(() => setLoadingMap(prev => ({ ...prev, [def.key]: false })));
    });
  }, [subjects]); // eslint-disable-line

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">📈 Verifications Report</h2>
          <p className="page-subtitle">Faculty-wise verified &amp; approved question counts per subject</p>
        </div>
      </div>

      {REPORT_DEFS.map(def => {
        const data    = reportsData[def.key];
        const loading = loadingMap[def.key];
        const error   = errorMap[def.key];
        // FIX: totalV/totalA derived only from `data` (faculty array),
        // not from an undefined `subject` variable.
        const totalV  = data?.faculty?.reduce((s, f) => s + f.verified, 0) || 0;
        const totalA  = data?.faculty?.reduce((s, f) => s + f.approved, 0) || 0;

        return (
          <div key={def.key} style={{ marginBottom:36 }}>
            <h3 style={{
              fontSize:15, fontWeight:800, color:'var(--text)',
              marginBottom:12, display:'flex', alignItems:'center', gap:8,
            }}>
              {def.icon} {def.label} Verification Report
            </h3>

            {error && <div className="error-box">{error}</div>}

            {(loading || data) && (
              <div className="vrep-kpi-row">
                <div className="vrep-kpi-card vrep-kpi-teal">
                  <div className="vrep-kpi-icon vrep-kpi-icon--teal">📚</div>
                  <div className="vrep-kpi-body">
                    <span className="vrep-kpi-label">Total Questions</span>
                    <span className="vrep-kpi-value">{loading ? '…' : data?.total_questions?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="vrep-kpi-card vrep-kpi-blue">
                  <div className="vrep-kpi-icon vrep-kpi-icon--green">✅</div>
                  <div className="vrep-kpi-body">
                    <span className="vrep-kpi-label">Verified</span>
                    <span className="vrep-kpi-value">{loading ? '…' : data?.total_verified?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="vrep-kpi-card vrep-kpi-purple">
                  <div className="vrep-kpi-icon vrep-kpi-icon--purple">🛡️</div>
                  <div className="vrep-kpi-body">
                    <span className="vrep-kpi-label">HOD Approved</span>
                    <span className="vrep-kpi-value">{loading ? '…' : data?.total_approved?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="vrep-kpi-card vrep-kpi-orange">
                  <div className="vrep-kpi-icon">⏳</div>
                  <div className="vrep-kpi-body">
                    <span className="vrep-kpi-label">Remaining</span>
                    <span className="vrep-kpi-value">
                      {loading ? '…' : ((data?.total_questions || 0) - (data?.total_approved || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="table-container vrep-table-container">
              <div className="vrep-table-header">
                <span className="vrep-table-header-icon">🗒️</span>
                <span className="vrep-table-header-title">Verification Summary</span>
              </div>
              <div className="table-scroll vrep-report-scroll">
                <table>
                  <colgroup>
                    <col className="col-num" />
                    <col className="vrep-col-faculty" />
                    <col className="vrep-col-count" />
                    <col className="vrep-col-count" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="col-num" rowSpan={2}>Sl. No.</th>
                      <th className="vrep-col-faculty" rowSpan={2}>Faculty Name</th>
                      <th className="vrep-group-th" colSpan={2}>No. of Questions</th>
                    </tr>
                    <tr>
                      <th className="vrep-col-count">Verified</th>
                      <th className="vrep-col-count">Approved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={4} className="loading-text">Loading…</td></tr>}
                    {!loading && !error && (!data || data.faculty?.length === 0) && (
                      <tr>
                        <td colSpan={4} className="vrep-empty-cell">
                          <div className="vrep-empty-state">
                            <span className="vrep-empty-icon">📂</span>
                            <p className="vrep-empty-title">No Data Available</p>
                            <p className="vrep-empty-sub">No Faculty Has Verified Questions In This Subject Yet.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {!loading && data?.faculty?.map((f, i) => (
                      <tr key={f.user_id}>
                        <td className="col-num">{i + 1}</td>
                        <td className="vrep-col-faculty">{f.name}</td>
                        <td className="vrep-col-count">{f.verified}</td>
                        <td className="vrep-col-count">{f.approved}</td>
                      </tr>
                    ))}
                    {!loading && data?.faculty?.length > 0 && (
                      <tr className="vrep-total-row">
                        <td className="col-num"></td>
                        <td className="vrep-col-faculty">Total</td>
                        <td className="vrep-col-count vrep-total-verified">{totalV}</td>
                        <td className="vrep-col-count vrep-total-approved">{totalA}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main AdminDashboard ───────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [activePage, setActivePage] = useState('dashboard');
  const [stats, setStats]           = useState({ questions:0, users:0, subjects:[], subjectCounts:[] });
  const navigate                    = useNavigate();
  const user                        = JSON.parse(localStorage.getItem('user'));

  const [reportsExpanded,   setReportsExpanded]   = useState(false);
  const [selectedReportKey, setSelectedReportKey] = useState(null);

  const REPORT_SUBMENU = [
    { key:'verifications', label:'Verifications'  },
    { key:'feedback',      label:'Feedbacks' },
  ];

  useEffect(() => { fetchStats(); }, []); // eslint-disable-line

  const fetchStats = async () => {
    try {
      const u = JSON.parse(localStorage.getItem('user'));
      if (u?.role === 'admin') {
        const [q, us, subjectsRes] = await Promise.all([
          API.get('/api/questions').catch(() => ({ data:[] })),
          API.get('/api/users').catch(() => ({ data:[] })),
          API.get('/api/subjects').catch(() => ({ data:[] })),
        ]);
        const allQuestions = Array.isArray(q.data) ? q.data : [];
        const allSubjects  = Array.isArray(subjectsRes.data) ? subjectsRes.data : [];
        const countMap = {};
        allQuestions.forEach(q => { const sid = q.subject_id; countMap[sid] = (countMap[sid] || 0) + 1; });
        setStats({
          questions: allQuestions.length,
          users: Array.isArray(us.data) ? us.data.length : 0,
          subjects: [],
          subjectCounts: allSubjects.map(s => ({ subject_id:s.subject_id, name:s.name, count:countMap[s.subject_id]||0 })),
        });
      } else {
        const verifyStats = await API.get('/api/verify/stats').catch(() => ({ data:{ subjects:[], total:0 } }));
        setStats({ questions: verifyStats.data.total||0, users:0, subjects: verifyStats.data.subjects||[], subjectCounts:[] });
      }
    } catch (err) { console.error(err); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const allMenuItems = [
    { key:'dashboard',     label:'📊 Dashboard',           roles:['admin','content_author'] },
    { key:'users',         label:'👥 Users',                roles:['admin'] },
    { key:'questions',     label:'📝 Question Bank',        roles:['admin','content_author'] },
    { key:'upload',        label:'📤 Upload Papers',        roles:['admin','content_author'] },
    { key:'verifications', label:'🔍 Verification History', roles:['admin'] },
    { key:'reports',       label:'📈 Reports',              roles:['admin'] },
  ];

  const menuItems = allMenuItems.filter(m => m.roles.includes(user?.role));

  useEffect(() => {
    const allowed = menuItems.map(m => m.key);
    if (!allowed.includes(activePage) && activePage !== 'reports-menu' && activePage !== 'reports')
      setActivePage(allowed[0] || 'dashboard');
  }, [user?.role]); // eslint-disable-line

  const subjectMeta = {
    chemistry  : { icon:'⚗️' },
    physics    : { icon:'⚡' },
    mathematics: { icon:'📐' },
    maths      : { icon:'📐' },
    biology    : { icon:'🧬' },
  };
  const getSubjectMeta = name => {
    const key = (name || '').toLowerCase();
    for (const k of Object.keys(subjectMeta)) { if (key.includes(k)) return subjectMeta[k]; }
    return { icon:'📚' };
  };

  const handleMenuClick = (item) => {
    if (item.key === 'reports') {
      setReportsExpanded(prev => !prev);
      setActivePage('reports-menu');
      setSelectedReportKey(null);
    } else {
      setActivePage(item.key);
      setReportsExpanded(false);
      setSelectedReportKey(null);
    }
  };

  const handleReportSubmenuClick = (r) => {
    setSelectedReportKey(r.key);
    setActivePage('reports');
  };

  return (
    <div className="dashboard-container">
      <div className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-row">
            <img src={JIT_LOGO} alt="JIT Logo" className="sidebar-logo"
              onError={e => e.target.style.display = 'none'} />
            <h2 className="sidebar-jast">JAST</h2>
          </div>
          <p className="sidebar-college">Jyothy Institute of Technology</p>
        </div>

        <ul className="sidebar-menu">
          {menuItems.map(item => (
            <React.Fragment key={item.key}>
              <li
                className={
                  item.key === 'reports'
                    ? (activePage === 'reports-menu' || activePage === 'reports')
                      ? reportsExpanded ? 'active reports-expanded' : 'active'
                      : ''
                    : activePage === item.key ? 'active' : ''
                }
                onClick={() => handleMenuClick(item)}
              >
                {item.label}
              </li>
              {item.key === 'reports' && reportsExpanded && (
                <ul className="sidebar-submenu">
                  {REPORT_SUBMENU.map(r => (
                    <li
                      key={r.key}
                      className={activePage === 'reports' && selectedReportKey === r.key ? 'active' : ''}
                      onClick={() => handleReportSubmenuClick(r)}
                    >
                      {r.label}
                    </li>
                  ))}
                </ul>
              )}
            </React.Fragment>
          ))}
        </ul>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{user?.name?.charAt(0).toUpperCase()}</div>
            <div className="sidebar-user-info">
              <p>{user?.name}</p>
              <span className="role-badge">{user?.role === 'admin' ? 'Administrator' : user?.role}</span>
            </div>
            <button className="logout-btn" onClick={handleLogout} title="Logout">⬅</button>
          </div>
        </div>
      </div>

      <div className="main-content">

        {activePage === 'dashboard' && (
          <>
            <div className="dash-welcome-banner">
              <div className="dash-welcome-left">
                <div className="dash-welcome-avatar">{user?.name?.charAt(0).toUpperCase()}</div>
                <div>
                  <h1 className="dash-welcome-title">Welcome Back, {user?.name}!</h1>
                  <p className="dash-welcome-sub">JAST — Jyothy Admission Scholarship Test Portal</p>
                </div>
              </div>
              <div className="dash-welcome-badge">
                {user?.role === 'admin' ? '🛡️ Administrator' : '✍️ Content Author'}
              </div>
            </div>

            <div className="dash-kpi-row">
              {user?.role === 'admin' && (
                <div className="dash-kpi-card dash-kpi-purple">
                  <div className="dash-kpi-icon">👥</div>
                  <div className="dash-kpi-body">
                    <span className="dash-kpi-label">Total Users</span>
                    <span className="dash-kpi-value">{stats.users}</span>
                  </div>
                </div>
              )}
              <div className="dash-kpi-card dash-kpi-teal">
                <div className="dash-kpi-icon">📚</div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-label">Subjects</span>
                  <span className="dash-kpi-value">{stats.subjectCounts.length || stats.subjects?.length || 0}</span>
                </div>
              </div>
            </div>

            {user?.role === 'admin' && stats.subjectCounts.length > 0 && (
              <div className="dash-section">
                <div className="dash-section-header">
                  <span className="dash-section-title">📊 Subject-Wise Question Bank Breakdown</span>
                </div>
                <div className="dash-subject-grid">
                  {stats.subjectCounts.map((s, idx) => {
                    const meta   = getSubjectMeta(s.name);
                    const colors = ['dash-sub-blue','dash-sub-green','dash-sub-purple','dash-sub-orange'];
                    return (
                      <div key={s.subject_id} className={`dash-subject-card ${colors[idx % colors.length]}`}>
                        <div className="dash-sub-top">
                          <span className="dash-sub-icon">{meta.icon}</span>
                          <span className="dash-sub-name">{s.name}</span>
                        </div>
                        <div className="dash-sub-count">{s.count.toLocaleString()}</div>
                      </div>
                    );
                  })}
                  <div className="dash-subject-card dash-sub-orange">
                    <div className="dash-sub-top">
                      <span className="dash-sub-icon">📝</span>
                      <span className="dash-sub-name">Total Questions</span>
                    </div>
                    <div className="dash-sub-count">{stats.questions.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            )}

            {user?.role === 'content_author' && (
              <>
                <div className="dash-kpi-row" style={{ marginTop:0 }}>
                  <div className="dash-kpi-card dash-kpi-blue">
                    <div className="dash-kpi-icon">📝</div>
                    <div className="dash-kpi-body">
                      <span className="dash-kpi-label">Total Questions</span>
                      <span className="dash-kpi-value">{stats.questions.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                {stats.subjects?.length > 0 && (
                  <div className="dash-section">
                    <div className="dash-section-header">
                      <span className="dash-section-title">📚 Your Subject Overview</span>
                    </div>
                    <div className="dash-subject-grid">
                      {stats.subjects.map((s, idx) => {
                        const pct    = s.total > 0 ? Math.round((s.verified / s.total) * 100) : 0;
                        const colors = ['dash-sub-blue','dash-sub-green','dash-sub-purple','dash-sub-orange'];
                        return (
                          <div key={s.subject_id} className={`dash-subject-card ${colors[idx % colors.length]}`}>
                            <div className="dash-sub-top">
                              <span className="dash-sub-icon">{getSubjectMeta(s.name).icon}</span>
                              <span className="dash-sub-name">{s.name}</span>
                            </div>
                            <div className="dash-sub-count">{s.total}</div>
                            <div className="dash-sub-label">Total Questions</div>
                            <div className="dash-sub-bar-track">
                              <div className="dash-sub-bar-fill" style={{ width:`${pct}%` }} />
                            </div>
                            <div className="dash-sub-pct">{pct}% Verified &nbsp;·&nbsp; {parseInt(s.total) - parseInt(s.verified)} Pending</div>
                            <div className="dash-sub-verify-row">
                              <span className="dash-chip dash-chip-green">✅ {s.verified} Verified</span>
                              <span className="dash-chip dash-chip-red">⏳ {parseInt(s.total) - parseInt(s.verified)} Pending</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activePage === 'users'         && <Users />}
        {activePage === 'questions'     && <Questions />}
        {activePage === 'upload'        && <Upload />}
        {activePage === 'verifications' && <VerificationsPanel />}
        {activePage === 'reports' && selectedReportKey === 'verifications' && <ReportsPanel />}
        {activePage === 'reports' && selectedReportKey === 'feedback'      && <FeedbackReportPanel />}

        {activePage === 'reports-menu' && (
          <div className="vrep-pick-prompt">
            <span className="vrep-pick-icon">📈</span>
            <p className="vrep-pick-text">
              Select a report type from the <strong>Reports</strong> menu on the left.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}